import React, { useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { FormCard } from "@/forms/FormCard";
import { PAYMENT_PROVIDERS } from "@cappelendamm/e-commerce-services";
import RadioGroup from "@/components/Radio/RadioGroup";
import { getNetsOptionDescription } from "@/checkout/Sections/PaymentSection/getNetsOptionDescription";
import MastercardIcon from "@/assets/payment-2/Mastercard.svg";
import VisaIcon from "@/assets/payment-2/Visa.svg";
import VippsIcon from "@/assets/payment-2/Vipps.svg";
import { InfoBox } from "@cappelendamm/design-system/react";
import { PiWarningCircle } from "react-icons/pi";
import { InputField } from "@/forms/InputField";
import { CheckoutForm } from "@/checkout/types";

type PaymentSectionProps = {
  isPending: boolean;
  immediateReservation: boolean;
  netsDisabled?: boolean;
  vippsInNetsForUnitSale?: boolean;
  invoiceOption?: boolean;
  useEhf?: boolean;
  preorderInvoiceOnly?: boolean;
  hasConflictingPaymentConstraints?: boolean;
};

export const PaymentSection = ({
  isPending,
  immediateReservation,
  netsDisabled,
  invoiceOption = true,
  vippsInNetsForUnitSale,
  useEhf,
  preorderInvoiceOnly,
  hasConflictingPaymentConstraints,
}: PaymentSectionProps) => {
  const {
    control,
    setValue,
    register,
    formState: { errors },
  } = useFormContext<CheckoutForm>();
  const paymentType = useWatch({ control, name: "payment.type" });

  let showNetsOption = true;
  if (!process.env.NEXT_PUBLIC_NETS_CHECKOUT_SDK_URL) {
    showNetsOption = false;
  }
  if (netsDisabled) {
    showNetsOption = false;
  }
  if (preorderInvoiceOnly && !hasConflictingPaymentConstraints) {
    showNetsOption = false;
  }
  if (hasConflictingPaymentConstraints && !netsDisabled) {
    showNetsOption = true;
  }
  if (!showNetsOption && !invoiceOption)
    throw new Error("Checkout without payment options!");

  useEffect(() => {
    if (hasConflictingPaymentConstraints) {
      setValue("payment.type", "" as never);
      return;
    }
    if (!invoiceOption && paymentType === PAYMENT_PROVIDERS.INVOICE) {
      setValue("payment.type", PAYMENT_PROVIDERS.NETS);
    }
  }, [invoiceOption, paymentType, setValue, hasConflictingPaymentConstraints]);

  useEffect(() => {
    if (hasConflictingPaymentConstraints) return;
    if (preorderInvoiceOnly && paymentType !== PAYMENT_PROVIDERS.INVOICE) {
      setValue("payment.type", PAYMENT_PROVIDERS.INVOICE);
    }
  }, [
    preorderInvoiceOnly,
    paymentType,
    setValue,
    hasConflictingPaymentConstraints,
  ]);

  return (
    <FormCard>
      <RadioGroup
        title="Betalingsmåte"
        options={[
          ...(showNetsOption
            ? [
                {
                  key: PAYMENT_PROVIDERS.NETS,
                  label: "Kort",
                  description: getNetsOptionDescription(immediateReservation),
                  icon: (
                    <div className="flex flex-wrap gap-2">
                      <div className="px-[3.5px]">
                        <MastercardIcon alt="Mastercard ikon" height={24} />
                      </div>
                      <div className="flex items-center">
                        <VisaIcon alt="Visa ikon" height={24} />
                      </div>
                      {vippsInNetsForUnitSale && (
                        <div className="flex items-center">
                          <VippsIcon alt="Vipps ikon" height={24} />
                        </div>
                      )}
                    </div>
                  ),
                  chipText: "Trygt og enkelt",

                  input: {
                    ...register("payment.type"),
                    value: PAYMENT_PROVIDERS.NETS,
                    disabled: isPending || hasConflictingPaymentConstraints,
                  },
                },
              ]
            : []),

          {
            key: PAYMENT_PROVIDERS.INVOICE,
            label: useEhf ? "Faktura med EHF" : "Faktura",
            description: "Betal senere med faktura fra Cappelen Damm.",
            input: {
              ...register("payment.type"),
              value: PAYMENT_PROVIDERS.INVOICE,
              disabled: !invoiceOption || hasConflictingPaymentConstraints,
            },
          },
        ]}
      />
      {useEhf && paymentType === PAYMENT_PROVIDERS.INVOICE && (
        <InputField
          name={`ehfReference`}
          label="EHF referanse"
          register={register}
          error={errors.ehfReference}
          requiredMessage="Du må skrive inn en EHF referanse"
          maxLength={{
            value: 50,
            message: "EHF referanse kan ikke være lengre enn 50 tegn",
          }}
          inputProps={{ maxLength: 50 }}
        />
      )}
      {!invoiceOption && !hasConflictingPaymentConstraints && (
        <InfoBox className="mt-2 flex !cvr-info-box items-center gap-4">
          <PiWarningCircle className="min-w-8" size={38} />
          <p>
            Ved kjøp av digitale produkter vil det kun være mulig å betale med
            kort.
          </p>
        </InfoBox>
      )}
      {preorderInvoiceOnly && !hasConflictingPaymentConstraints && (
        <InfoBox className="mt-2 flex !cvr-info-box items-center gap-4">
          <PiWarningCircle className="min-w-8" size={38} />
          <p>
            Handlekurven inneholder forhåndsbestillinger som utgis om mer enn 30
            dager. Disse kan kun betales med faktura. Du belastes ikke før boken
            sendes.
          </p>
        </InfoBox>
      )}
      {hasConflictingPaymentConstraints && (
        <div className="mt-2 flex items-center gap-4 rounded border border-status-warning-500 bg-status-warning-100 px-4 py-5 !pb-5 text-gray-700">
          <PiWarningCircle className="shrink-0" size={24} />
          <p>
            Varene du har i handlekurven kan ikke betales samtidig. Vennligst
            fjern det ene produktet for å velge betalingsmåte.
          </p>
        </div>
      )}
    </FormCard>
  );
};
